// src/components/AGTFit/workout/TodaysActivity.js
import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  useTheme,
  Snackbar,
  Alert,
  Tooltip,
  Collapse
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Dumbbell,
  ChevronRight,
  Plus,
  RefreshCw,
  Minus,
  Calendar,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import ExerciseExplorer from './explorer/ExerciseExplorer';
import LogExerciseDialog from './exercisesVisual/LogExerciseDialog';
import EditExerciseDialog from './exercisesVisual/EditExerciseDialog';
import agtfitService from '../service/agtfitService';

// Función para debounce (retrasar la ejecución hasta que pase un tiempo sin llamadas) - Unused function
// const debounce = (func, wait) => {
//   let timeout;
//
//   return function executedFunction(...args) {
//     const later = () => {
//       clearTimeout(timeout);
//       func(...args);
//     };
//
//     clearTimeout(timeout);
//     timeout = setTimeout(later, wait);
//   };
// };

// Color principal
const FIT_ORANGE = '#FF6B00';

// Colores para modo wallpaper
const WALLPAPER_COLORS = {
  bg: 'rgba(128, 128, 128, 0.3)',
  bgLight: 'rgba(128, 128, 128, 0.25)',
  border: 'rgba(128, 128, 128, 0.3)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)'
};

// Componentes estilizados (sin cambios)...
const StatsCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  padding: theme.spacing(3),
  height: '100%',
  boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : '0 2px 10px rgba(0,0,0,0.05)',
  border: `1px solid ${theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.border
    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)')}`,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bg
    : (theme.palette.mode === 'dark' ? '#1A1A1A' : '#FFFFFF'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  }
}));

// Unused styled component - FoodItemCard
// const FoodItemCard = styled(Box)(({ theme }) => ({
//   padding: theme.spacing(2),
//   borderRadius: 12,
//   backgroundColor: theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7',
//   marginBottom: theme.spacing(2),
//   display: 'flex',
//   alignItems: 'center',
//   justifyContent: 'space-between',
// }));

const ExerciseItemCard = styled(Box)(({ theme, fromWorkout }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? (fromWorkout ? 'rgba(255,107,0,0.2)' : WALLPAPER_COLORS.bgLight)
    : (fromWorkout
      ? theme.palette.mode === 'dark' ? 'rgba(255,107,0,0.15)' : 'rgba(255,107,0,0.05)'
      : theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  transition: 'transform 0.2s, box-shadow 0.2s',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.actualMode === 'wallpaper'
      ? 'none'
      : (theme.palette.mode === 'dark'
        ? '0 6px 10px rgba(0,0,0,0.3)'
        : '0 6px 10px rgba(0,0,0,0.1)'),
  }
}));

// Nuevo componente para los ejercicios dentro de un entrenamiento expandido
const WorkoutExerciseItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: 8,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  marginBottom: theme.spacing(1),
  marginLeft: theme.spacing(2),
  marginRight: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const IconBox = styled(Box)(({ theme, color = FIT_ORANGE, fromWorkout }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: fromWorkout
    ? theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.2)'
    : theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(2),
  color: color,
}));

// Icono más pequeño para los ejercicios dentro de un entrenamiento
const SmallIconBox = styled(Box)(({ theme }) => ({
  width: 28,
  height: 28,
  borderRadius: '50%',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(2),
  color: FIT_ORANGE,
}));

const OutlineButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5, 3),
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '15px',
  boxShadow: 'none',
  border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : FIT_ORANGE,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  '&:hover': {
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? 'rgba(255,107,0,0.15)'
      : (theme.palette.mode === 'dark' ? '#3C3C3E' : '#E5E5E7'),
  },
  '&:focus': {
    outline: 'none',
  },
  '&.MuiButton-root:hover': {
    boxShadow: 'none',
    border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 2),
    fontSize: '14px',
  }
}));

const WaterControlButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  minWidth: 'auto',
  width: 32,
  height: 32,
  padding: 0,
  textTransform: 'none',
  boxShadow: 'none',
  border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : '#2196F3',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  '&:hover': {
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? 'rgba(33, 150, 243, 0.15)'
      : (theme.palette.mode === 'dark' ? '#3C3C3E' : '#E5E5E7'),
  },
  '&:focus': {
    outline: 'none',
  },
  '&.MuiButton-root:hover': {
    boxShadow: 'none',
    border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  }
}));

const WaterControlsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start', // Para alinear desde la izquierda
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
  width: '100%', 
  left: 0,
  // Añadimos un margen izquierdo para centrar parcialmente - ajusta este valor según necesites
  marginLeft: '-15%', // Esto mueve los botones aproximadamente un 20% desde la izquierda
}));

const CircularProgressContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'inline-flex',
  marginRight: theme.spacing(2),
}));

const CircularProgressLabel = styled(Box)(({ theme }) => ({
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
}));

const WaterCircularProgressLabel = styled(Box)(({ theme }) => ({
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  padding: theme.spacing(0.5),
}));

const WorkoutBadge = styled(Typography)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  backgroundColor: FIT_ORANGE,
  color: 'white',
  padding: theme.spacing(0.25, 0.75),
  borderRadius: theme.shape.borderRadius,
  fontSize: '0.6rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  display: 'inline-block',
  verticalAlign: 'middle',
}));

// Función para calcular porcentajes para las barras de progreso
const getPercentage = (consumed, goal) => {
  if (!goal) return 0;
  const percentage = (consumed / goal) * 100;
  return Math.min(percentage, 100); // Limitar a 100%
};

// Componente principal
const TodaysActivity = forwardRef(({ stats = {}, todayExercises = [], onStartWorkout, onExerciseAdded, onStatsUpdated, onCaloriesBurnedChange, selectedDate }, ref) => {
  const theme = useTheme();
  const [openExerciseExplorer, setOpenExerciseExplorer] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [openLogDialog, setOpenLogDialog] = useState(false);
  const [exercises, setExercises] = useState(todayExercises || []);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
 
  const [selectedExerciseToEdit, setSelectedExerciseToEdit] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [showWaterInLiters, setShowWaterInLiters] = useState(false);
  const [localStats, setLocalStats] = useState({
    steps: { count: 0, goal: 10000 },
    water: { consumed: 0, goal: 8 },
    calories: { burned: 0, goal: 0 } // Inicializar con 0, no con 500
  });
  
  // Estado para controlar qué entrenamientos están expandidos
  const [expandedWorkouts, setExpandedWorkouts] = useState({});
  
  // Referencia para el último valor de calorías quemadas (currently unused)
  // const lastBurnedCalories = useRef(0);
  
  // Actualizar ejercicios cuando se actualiza todayExercises
  useEffect(() => {
    if (todayExercises && todayExercises.length > 0) {
      setExercises(todayExercises);
    }
  }, [todayExercises]);

  // Función para cargar los ejercicios del día
  const fetchTodayExercises = useCallback(async (skipProfileUpdate = false) => {
    try {
      setLoading(true);
      // Usar la fecha seleccionada en lugar de la fecha actual
      const date = selectedDate ? selectedDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
      const response = await agtfitService.getExercises(date);

      if (response.success && Array.isArray(response.data)) {
        const exercisesList = response.data;
        setExercises(exercisesList);

        // Obtener las calorías quemadas totales de la respuesta
        const totalCaloriesBurned = response.totalCaloriesBurned || exercisesList.reduce((total, exercise) => {
          return total + (exercise.caloriesBurned || 0);
        }, 0);

        // Actualizar el estado local PRESERVANDO el objetivo de calorías
        const updatedStats = {
          ...localStats,
          calories: {
            ...localStats.calories,
            burned: totalCaloriesBurned,
            goal: localStats.calories.goal // MANTENER el objetivo existente
          }
        };

        setLocalStats(updatedStats);

        // Notificar al componente padre
        if (onStatsUpdated) {
          // Asegurarse de que el objetivo de calorías se preserve al notificar al padre
          onStatsUpdated({
            ...updatedStats,
            calories: {
              ...updatedStats.calories,
              goal: updatedStats.calories.goal // MANTENER el objetivo existente
            }
          });
        }

        // Notificar al componente padre sobre el cambio en calorías quemadas
        if (onCaloriesBurnedChange) {
          onCaloriesBurnedChange(totalCaloriesBurned);
        }
      }
    } catch (error) {
      console.error('Error fetching exercises:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load exercises',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [selectedDate, localStats, onStatsUpdated, onCaloriesBurnedChange]);

  // Cargar ejercicios al montar el componente si no se proporcionaron
  useEffect(() => {
    if (!todayExercises || todayExercises.length === 0) {
      fetchTodayExercises();
    }
  }, [selectedDate, todayExercises, fetchTodayExercises]);
 
  // Actualizar localStats cuando cambian los stats de entrada
  useEffect(() => {
    // Combinar stats proporcionados con valores por defecto
    const defaultStats = {
      steps: { count: 0, goal: 10000 },
      water: { consumed: 0, goal: 8 },
      calories: { burned: 0, goal: 0 }
    };
   
    // IMPORTANTE: Preservar el objetivo de calorías existente
    const merged = {
      steps: { ...defaultStats.steps, ...stats.steps },
      water: { ...defaultStats.water, ...stats.water },
      calories: { 
        ...defaultStats.calories, 
        ...stats.calories,
        goal: stats.calories?.goal || defaultStats.calories.goal
      }
    };
   
    setLocalStats(merged);
  }, [stats]);

  // Función para manejar la selección de un ejercicio del explorador
  const handleSelectExercise = (exercise) => {
    setSelectedExercise(exercise);
    setOpenExerciseExplorer(false);
    setOpenLogDialog(true);
  };
 
  // Función para manejar el cierre del diálogo de registro
  const handleLogDialogClose = async (success, exerciseData) => {
    setOpenLogDialog(false);
   
    if (success && exerciseData) {
      try {
        setLoading(true);

        // Si se proporciona una fecha seleccionada, usarla en el ejercicio
        if (selectedDate) {
          exerciseData.date = selectedDate.toISOString().split('T')[0];
        }
       
        // Registrar el ejercicio
        const response = await agtfitService.logExercise(exerciseData);
       
        if (response.success) {
          // Actualizar la lista de ejercicios
          await fetchTodayExercises();
         
          // Notificar al componente padre si existe la función
          if (onExerciseAdded) {
            onExerciseAdded(response.data);
          }
         
          setSnackbar({
            open: true,
            message: 'Exercise logged successfully!',
            severity: 'success'
          });
        } else {
          throw new Error(response.error || 'Failed to log exercise');
        }
      } catch (error) {
        console.error('Error logging exercise:', error);
        setSnackbar({
          open: true,
          message: 'Failed to log exercise',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    }
  };
 
  // Función para manejar el clic en un ejercicio de la lista
  const handleExerciseClick = (exercise, event) => {
    // Detener la propagación del evento para evitar comportamientos inesperados
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
 
    // No abrir el diálogo de edición si el ejercicio viene de un entrenamiento
    if (exercise.fromWorkout) {
      setSnackbar({
        open: true,
        message: 'This exercise is part of a workout and cannot be edited individually',
        severity: 'info'
      });
      return;
    }
    
    // Usar setTimeout para asegurar que el estado se actualiza correctamente
    setTimeout(() => {
      setSelectedExerciseToEdit(exercise);
      setOpenEditDialog(true);
    }, 10);
  };
 
  // Función para manejar el cierre del diálogo de edición
  const handleEditDialogClose = async (success, exerciseData, action) => {
    setOpenEditDialog(false);
   
    if (success) {
      try {
        setLoading(true);
        
        // Simplemente recargar los ejercicios, sin preocuparse por actualizar el perfil global
        await fetchTodayExercises();
        
        // Notificar al componente padre si existe la función
        if (onExerciseAdded) {
          onExerciseAdded(exerciseData);
        }
        
        setSnackbar({
          open: true,
          message: action === 'delete'
            ? 'Exercise deleted successfully!'
            : 'Exercise updated successfully!',
          severity: 'success'
        });
      } catch (error) {
        console.error(`Error ${action === 'delete' ? 'deleting' : 'updating'} exercise:`, error);
        setSnackbar({
          open: true,
          message: `Failed to ${action === 'delete' ? 'delete' : 'update'} exercise`,
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    }
  };
 
  // Función para cerrar el snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };
 
  // Función para alternar entre visualización de vasos y litros
  const toggleWaterUnit = () => {
    setShowWaterInLiters(!showWaterInLiters);
  };
 
  // Nuevas funciones para incrementar y decrementar el agua
  const incrementWater = () => {
    const updatedStats = { ...localStats };
   
    if (showWaterInLiters) {
      // Incrementar en 0.1L (equivalente a 0.4 vasos)
      const incrementInGlasses = 0.4; // 0.1L / 0.25L por vaso
      updatedStats.water.consumed = Math.min(
        updatedStats.water.consumed + incrementInGlasses,
        updatedStats.water.goal
      );
    } else {
      // Incrementar en 1 vaso
      updatedStats.water.consumed = Math.min(
        updatedStats.water.consumed + 1,
        updatedStats.water.goal
      );
    }
   
    setLocalStats(updatedStats);
   
    // Notificar al componente padre si existe la función
    if (onStatsUpdated) {
      onStatsUpdated(updatedStats);
    }
  };
 
  const decrementWater = () => {
    const updatedStats = { ...localStats };
   
    if (showWaterInLiters) {
      // Decrementar en 0.1L (equivalente a 0.4 vasos)
      const decrementInGlasses = 0.4; // 0.1L / 0.25L por vaso
      updatedStats.water.consumed = Math.max(
        updatedStats.water.consumed - decrementInGlasses,
        0
      );
    } else {
      // Decrementar en 1 vaso
      updatedStats.water.consumed = Math.max(
        updatedStats.water.consumed - 1,
        0
      );
    }
   
    setLocalStats(updatedStats);
   
    // Notificar al componente padre si existe la función
    if (onStatsUpdated) {
      onStatsUpdated(updatedStats);
    }
  };

  // Función para actualizar las calorías desde un entrenamiento
  const updateCaloriesFromWorkout = (caloriesBurned) => {
    if (caloriesBurned > 0) {
      const updatedStats = {
        ...localStats,
        calories: {
          ...localStats.calories,
          burned: localStats.calories.burned + caloriesBurned,
          goal: localStats.calories.goal // MANTENER el objetivo existente
        }
      };
  
      setLocalStats(updatedStats);
  
      // Notificar al componente padre si existe la función
      if (onStatsUpdated) {
        onStatsUpdated(updatedStats);
      }
  
      // Notificar al componente padre sobre el cambio en calorías quemadas
      if (onCaloriesBurnedChange) {
        onCaloriesBurnedChange(updatedStats.calories.burned);
      }
  
      // Obtener la fecha formateada
      const formattedDate = selectedDate ? selectedDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
  
      // Actualizar las calorías quemadas para este día específico
      agtfitService.updateDayCaloriesBurned(formattedDate, updatedStats.calories.burned);
  
      // Recargar los ejercicios para incluir los del nuevo entrenamiento
      // Usamos un timeout para evitar múltiples solicitudes simultáneas
      setTimeout(() => {
        fetchTodayExercises(true); // Pasar true para evitar otra actualización del perfil
      }, 1000);
    }
  };
  
  // Exponer esta función para que pueda ser llamada desde componentes padres
  useImperativeHandle(ref, () => ({
    updateCaloriesFromWorkout
  }));

  // Función para alternar la expansión de un grupo de entrenamiento
  const toggleWorkoutExpansion = (workoutId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    setExpandedWorkouts(prev => ({
      ...prev,
      [workoutId]: !prev[workoutId]
    }));
  };

  // Función para renderizar los ejercicios agrupados
  const renderExercises = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={30} sx={{ color: FIT_ORANGE }} />
        </Box>
      );
    }

    if (exercises.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          No exercises logged today
        </Typography>
      );
    }

    // Separar ejercicios individuales y de entrenamiento
    const individualExercises = exercises.filter(ex => !ex.fromWorkout);
    const workoutExercises = exercises.filter(ex => ex.fromWorkout);

    // Agrupar los ejercicios de entrenamiento por workoutId
    const workoutGroups = {};
    workoutExercises.forEach(exercise => {
      const workoutId = exercise.workoutId || 'unknown';
      if (!workoutGroups[workoutId]) {
        workoutGroups[workoutId] = [];
      }
      workoutGroups[workoutId].push(exercise);
    });

    return (
      <>
        {/* Renderizar ejercicios individuales */}
        {individualExercises.map((exercise, index) => (
          <ExerciseItemCard
            key={`individual-${exercise.exerciseId || index}`}
            onClick={(e) => handleExerciseClick(exercise, e)}
            fromWorkout={false}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconBox fromWorkout={false}>
                <Dumbbell size={20} />
              </IconBox>
              <Box>
                <Typography variant="body1" fontWeight={500}>
                  {exercise.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {exercise.sets || 0} sets • {exercise.reps || 0} reps • {Math.round(exercise.caloriesBurned || 0)} kcal
                </Typography>
              </Box>
            </Box>
            <IconButton size="small">
              <ChevronRight size={16} />
            </IconButton>
          </ExerciseItemCard>
        ))}

        {/* Renderizar grupos de entrenamientos */}
        {Object.entries(workoutGroups).map(([workoutId, exercises]) => {
          // Calcular estadísticas del grupo de entrenamiento
          const totalCalories = exercises.reduce((sum, ex) => sum + (ex.caloriesBurned || 0), 0);
          const exerciseCount = exercises.length;
          const workoutName = exercises[0]?.workoutName || 'Workout Session';
          const isExpanded = expandedWorkouts[workoutId] || false;
          
          return (
            <Box key={`workout-${workoutId}`} sx={{ mb: 2 }}>
              <ExerciseItemCard
                fromWorkout={true}
                onClick={(e) => toggleWorkoutExpansion(workoutId, e)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconBox fromWorkout={true}>
                    <Calendar size={20} />
                  </IconBox>
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      {workoutName}
                      <WorkoutBadge component="span">
                        Workout
                      </WorkoutBadge>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {exerciseCount} exercise{exerciseCount !== 1 ? 's' : ''} • {Math.round(totalCalories)} kcal
                    </Typography>
                  </Box>
                </Box>
                <IconButton size="small">
                  {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </IconButton>
              </ExerciseItemCard>
              
              {/* Ejercicios dentro del entrenamiento (expandibles) */}
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ mb: 2, mt: -1 }}>
                  {exercises.map((exercise, index) => (
                    <WorkoutExerciseItem key={`workout-exercise-${workoutId}-${index}`}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <SmallIconBox>
                          <Dumbbell size={16} />
                        </SmallIconBox>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {exercise.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {exercise.sets || 0} sets • {exercise.reps || 0} reps • {Math.round(exercise.caloriesBurned || 0)} kcal
                          </Typography>
                        </Box>
                      </Box>
                    </WorkoutExerciseItem>
                  ))}
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </>
    );
  };

  return (
    <StatsCard>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconBox>
          <Dumbbell size={24} />
        </IconBox>
        <Typography variant="h6" fontWeight={600}>
          Today's Activity
        </Typography>
      </Box>
     
      <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 4 }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgressContainer>
            <CircularProgress
              variant="determinate"
              value={getPercentage(localStats.steps.count, localStats.steps.goal)}
              size={80}
              thickness={4}
              sx={{ color: FIT_ORANGE }}
            />
            <CircularProgressLabel>
              <Typography variant="caption" color="text.secondary">
                Steps
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {localStats.steps.count}
              </Typography>
            </CircularProgressLabel>
          </CircularProgressContainer>
        </Box>
       
        <Box sx={{ textAlign: 'center', position: 'relative' }}>
          <CircularProgressContainer>
            <CircularProgress
              variant="determinate"
              value={getPercentage(localStats.water.consumed, localStats.water.goal)}
              size={80}
              thickness={4}
              sx={{ color: '#2196F3' }}
            />
            <WaterCircularProgressLabel>
              <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                Water
              </Typography>
              <Typography 
                variant="body2" 
                fontWeight={600} 
                fontSize="0.7rem" 
                textAlign="center" 
                lineHeight={1} 
                sx={{ mt: 0.5 }}
              >
                {showWaterInLiters
                  ? `${(localStats.water.consumed * 0.25).toFixed(1)}/${(localStats.water.goal * 0.25).toFixed(1)}L`
                  : `${Math.round(localStats.water.consumed)}/${localStats.water.goal}`
                }
              </Typography>
            </WaterCircularProgressLabel>
          </CircularProgressContainer>
         
          {/* Botones de control mejorados y centrados */}
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 1 }}>
            <WaterControlsContainer>
              <Tooltip title={`Switch to ${showWaterInLiters ? 'glasses' : 'liters'}`}>
                <WaterControlButton onClick={toggleWaterUnit}>
                  <RefreshCw size={16} />
                </WaterControlButton>
              </Tooltip>
              <WaterControlButton
                onClick={decrementWater}
                disabled={localStats.water.consumed <= 0}
              >
                <Minus size={16} />
              </WaterControlButton>
              <WaterControlButton
                onClick={incrementWater}
                disabled={localStats.water.consumed >= localStats.water.goal}
              >
                <Plus size={16} />
              </WaterControlButton>
            </WaterControlsContainer>
          </Box>
        </Box>
       
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgressContainer>
            <CircularProgress
              variant="determinate"
              value={getPercentage(localStats.calories.burned, localStats.calories.goal)}
              size={80}
              thickness={4}
              sx={{ color: '#4CAF50' }}
            />
            <CircularProgressLabel>
              <Typography variant="caption" color="text.secondary">
                Calories
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {Math.round(localStats.calories.burned)}
              </Typography>
            </CircularProgressLabel>
          </CircularProgressContainer>
        </Box>
      </Box>
     
      <Divider sx={{ my: 2 }} />
     
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        Today's Exercises
      </Typography>
     
      {renderExercises()}
     
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
        <OutlineButton
          startIcon={<Plus size={16} />}
          variant="outlined"
          onClick={() => setOpenExerciseExplorer(true)}
        >
          Log Exercise
        </OutlineButton>
       
        <Button
          variant="text"
          endIcon={<ChevronRight size={16} />}
          sx={{
            color: theme.palette.text.secondary,
            textTransform: 'none'
          }}
          onClick={onStartWorkout}
        >
          See Workout
        </Button>
      </Box>
     
      {/* Diálogo para explorar ejercicios */}
      <ExerciseExplorer
        open={openExerciseExplorer}
        onClose={() => setOpenExerciseExplorer(false)}
        onSelectExercise={handleSelectExercise}
      />
     
      {/* Diálogo para registrar ejercicio */}
      <LogExerciseDialog
        open={openLogDialog}
        onClose={handleLogDialogClose}
        exercise={selectedExercise}
        selectedDate={selectedDate}
      />
     
      {/* Diálogo para editar ejercicio */}
      <EditExerciseDialog
        open={openEditDialog}
        onClose={handleEditDialogClose}
        exercise={selectedExerciseToEdit}
        selectedDate={selectedDate}
      />
     
      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </StatsCard>
  );
});

export default TodaysActivity;